import { useEffect, useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { recommendedDecksForBoss } from '../../recommended-decks/domain/recommendedDecks.js';
import { useRaidOverview } from '../hooks/useRaidOverview.js';
import { RaidAttackStatus } from '../shared/RaidAttackStatus.jsx';
import { RaidBossDetailModal } from '../shared/RaidBossDetailModal.jsx';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

const STATUS_LABELS = { draft: '설정 중', active: '활성', closed: '종료' };

function formatEok(value, digits = 3) {
  if (value === null || value === undefined) return '인피니티';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: digits })}억`;
}

function formatInfinityEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}억`;
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function formatSync(value) {
  if (!value) return '동기화 없음';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '시간 확인 불가';
  }
}

function RaidKpi({ label, value, sub, accent = false }) {
  return (
    <article className={`raid-home-kpi ${accent ? 'accent' : ''}`}>
      <span>{label}</span><strong>{value}</strong><small>{sub}</small>
    </article>
  );
}

function InfinityBossBody({ boss }) {
  return (
    <div className="raid-home-boss-body raid-home-infinity-body">
      <h3>{bossDisplayName(boss)}</h3>
      <div className="raid-home-infinity-meta">
        <span className="raid-home-infinity-phase">4단</span>
        <span className="raid-home-infinity-badge"><b aria-hidden="true">∞</b> 인피니티</span>
      </div>
      <span className="raid-home-infinity-label">누적 딜</span>
      <strong className="raid-home-infinity-damage">{formatInfinityEok(boss.currentDamageEok)}</strong>
      <div className="raid-home-hp-track infinite raid-home-infinity-track" aria-label="인피니티 체력">
        <span />
      </div>
      <div className="raid-home-infinity-note">
        <span className="raid-home-infinity-symbol" aria-hidden="true">∞</span>
        <div><strong>인피니티 보스</strong><small>전체 단수 평균 {formatEok(boss.averageDamageEok)} · 공격 제한 없음</small></div>
      </div>
    </div>
  );
}

function StandardBossBody({ boss }) {
  return (
    <div className="raid-home-boss-body">
      <h3>{bossDisplayName(boss)}</h3>
      <div className="raid-home-boss-meta"><span>현재 {boss.currentPhase}단</span><span>실전 {boss.attackCount}타</span></div>
      <div className="raid-home-hp-label"><span>남은 HP</span><b>{formatRaidHpEok(boss.remainingHpEok)}</b></div>
      <div className="raid-home-hp-track"><span style={{ width: `${boss.remainingPercent}%` }} /></div>
      <div className="raid-home-hp-raw">{formatRaidHpEok(boss.remainingHpEok)} / {formatRaidHpEok(boss.maxHpEok)}</div>
      <div className="raid-home-boss-mini">
        <div><span>전체 단수 평균 딜</span><small>실전 {boss.attackCount}타 기준</small></div>
        <strong>{formatEok(boss.averageDamageEok)}</strong>
      </div>
    </div>
  );
}

function BossCard({ boss, onOpen }) {
  return (
    <button className={`raid-home-boss-card ${boss.isCleared ? 'cleared' : ''} ${boss.isInfinite ? 'infinite' : ''}`} type="button" onClick={onOpen}>
      <div className="raid-home-boss-image" style={{ backgroundImage: boss.imageUrl ? `url(${siteImageUrl(boss.imageUrl)})` : undefined }}>
        <ElementIcon element={boss.element} className="raid-home-element-icon" />
        {boss.isCleared ? <strong className="raid-home-clear">CLEAR</strong> : null}
      </div>
      {boss.isInfinite ? <InfinityBossBody boss={boss} /> : <StandardBossBody boss={boss} />}
    </button>
  );
}

function InfinityBossFeature({ boss, onOpen }) {
  const backgroundImage = boss.imageUrl
    ? `linear-gradient(90deg,rgba(6,11,17,.98) 0%,rgba(7,15,22,.88) 32%,rgba(7,15,22,.36) 62%,rgba(5,11,16,.9) 100%),url(${siteImageUrl(boss.imageUrl)})`
    : undefined;
  return (
    <button
      className="raid-home-infinity-feature"
      type="button"
      style={{ backgroundImage }}
      onClick={onOpen}
      aria-label={`${bossDisplayName(boss)} 인피니티 상세 보기`}
    >
      <div className="raid-home-infinity-feature-copy">
        <span>INFINITY BOSS</span>
        <h3>{bossDisplayName(boss)}</h3>
        <div className="raid-home-infinity-feature-weak"><b>WEAK POINT</b><ElementIcon element={boss.element} /></div>
        <p>3단 전체 클리어 · 5보스 인피니티 진행</p>
      </div>
      <div className="raid-home-infinity-feature-stats">
        <div><span>현재 단계</span><strong>4단</strong></div>
        <div><span>누적 딜</span><strong>{formatInfinityEok(boss.currentDamageEok)}</strong></div>
      </div>
      <div className="raid-home-infinity-feature-mark">
        <span aria-hidden="true">∞</span>
        <strong>인피니티</strong>
        <small>공격 제한 없음</small>
      </div>
    </button>
  );
}

function recommendationSummary(decks) {
  if (!decks.length) return '추천 스쿼드 미등록';
  const titles = decks.slice(0, 2).map((deck) => deck.title).filter(Boolean);
  const remaining = decks.length - titles.length;
  return `${titles.join(' · ')}${remaining > 0 ? ` 외 ${remaining}개` : ''}`;
}

function RecommendationPanel({ bosses, decks = [] }) {
  return (
    <div className="raid-home-recommend-list">
      {bosses.map((boss) => {
        const matchedDecks = recommendedDecksForBoss(decks, boss);
        return (
          <div className="raid-home-recommend-row" key={boss.bossNo}>
            <div><span>{boss.element || '-'}</span><strong>{bossDisplayName(boss)}</strong><small>{matchedDecks.length ? `${matchedDecks.length}개` : '없음'}</small></div>
            <p title={matchedDecks.map((deck) => deck.title).join(' · ')}>{recommendationSummary(matchedDecks)}</p>
          </div>
        );
      })}
    </div>
  );
}

export function DesktopRaidOverview({ canManage = false, onNavigate = () => {} }) {
  const raid = useRaidOverview();
  const season = raid.selectedSeason;
  const [detailBossNo, setDetailBossNo] = useState(null);
  const [showAllBosses, setShowAllBosses] = useState(false);
  const selectedBoss = useMemo(
    () => season?.bosses?.find((boss) => boss.bossNo === detailBossNo) || null,
    [detailBossNo, season?.bosses],
  );

  useEffect(() => {
    setShowAllBosses(false);
  }, [season?.globalPhase, season?.seasonNo]);

  if (raid.loading) return <section className="raid-home-state"><span /><p>실전 기록을 계산하고 있습니다.</p></section>;
  if (raid.errorMessage) return <section className="raid-home-state error"><strong>레이드 현황을 불러오지 못했습니다.</strong><p>{raid.errorMessage}</p></section>;
  if (!season) return <section className="raid-home-state"><strong>표시할 레이드 차수가 없습니다.</strong><p>차수와 보스 설정을 먼저 저장해주세요.</p></section>;

  const infinityBoss = season.bosses.find((boss) => Number(boss.bossNo) === 5);
  const isInfinityStage = season.globalPhase === 4 && Boolean(infinityBoss);
  const stageHpText = isInfinityStage
    ? `${bossDisplayName(infinityBoss, 5)} 인피니티`
    : `${formatRaidHpEok(season.currentStageRemainingHpEok)} / ${formatRaidHpEok(season.currentStageTotalHpEok)}`;

  return (
    <section className="raid-home-desktop" aria-label="유니온 레이드 현황">
      <div className="raid-home-lobby">
        <section className="raid-home-status">
          <div className="raid-home-status-main">
            <div className="raid-home-season-badge"><strong>{season.seasonNo}</strong><span>SEASON</span></div>
            <div className="raid-home-status-copy">
              <span>HARD RAID DATA</span>
              <h1>{raid.unionName || '유니온'} 레이드 현황</h1>
              <p>하드 실전 기록 기준 · {isInfinityStage ? `${bossDisplayName(infinityBoss, 5)} 인피니티 진행` : `${season.globalPhase}단 진행 중`}</p>
              <div className="raid-home-stage-progress-head"><span>{season.globalPhase}단 기준 진행도</span><b>{formatPercent(season.stageProgressPercent)}</b></div>
              <div className="raid-home-stage-progress"><span style={{ width: `${season.stageProgressPercent}%` }} /></div>
              <small>{stageHpText}</small>
            </div>
            <div className="raid-home-season-control">
              <label><span>조회 차수</span><select value={season.seasonNo} onChange={(event) => raid.selectSeason(event.target.value)}>{raid.seasons.map((item) => <option key={item.seasonNo} value={item.seasonNo}>{item.seasonNo}차 · {STATUS_LABELS[item.status] || item.status}</option>)}</select></label>
              <RefreshIconButton onClick={() => raid.refresh(season.seasonNo)} />
              <small>{formatSync(season.lastSyncAt)}</small>
            </div>
          </div>
          <div className="raid-home-kpis">
            <RaidKpi label="현재 진행 단수" value={`${season.globalPhase}단`} sub="하드 실전 기준" />
            <RaidKpi label="총 멤버" value={`${season.memberCount}명`} sub="차수 멤버 기준" />
            <RaidKpi label="공격 타수" value={`${season.recordCount}/${season.totalAttackLimit}`} sub={`남은 ${season.remainingAttacks}타`} accent />
            <RaidKpi label="남은 총 HP" value={isInfinityStage ? '인피니티' : formatRaidHpEok(season.currentStageRemainingHpEok)} sub={`${formatPercent(season.stageProgressPercent)} 진행`} />
          </div>
        </section>

        <div className="raid-home-section-title">
          <h2>레이드 스테이지 현황</h2>
          <div className="raid-home-section-actions">
            {isInfinityStage ? <button className="raid-home-boss-view-toggle" type="button" aria-pressed={showAllBosses} onClick={() => setShowAllBosses((current) => !current)}>{showAllBosses ? '5보스만 보기' : '전체 보스 보기'}</button> : null}
            {canManage ? <><button type="button" onClick={() => onNavigate('tetris')}>테트리스</button><button type="button" onClick={() => onNavigate('management/season')}>보스 설정</button></> : null}
          </div>
        </div>

        {isInfinityStage && !showAllBosses ? (
          <div className="raid-home-infinity-focus">
            <InfinityBossFeature boss={infinityBoss} onOpen={() => setDetailBossNo(infinityBoss.bossNo)} />
          </div>
        ) : (
          <div className="raid-home-boss-grid">
            {season.bosses.map((boss) => <BossCard boss={boss} key={boss.bossNo} onOpen={() => setDetailBossNo(boss.bossNo)} />)}
          </div>
        )}

        <div className="raid-home-bottom-grid">
          <article className="raid-home-panel">
            <header><h3>상위 딜러</h3><span>TOP 5</span></header>
            <div className="raid-home-rank-list">
              {season.topDealers.length ? season.topDealers.map((member, index) => (
                <div key={member.openid}><span>{index + 1}</span><strong>{member.nickname}</strong><em>{formatEok(member.totalDamageEok)}</em></div>
              )) : <p>실전 기록 없음</p>}
            </div>
          </article>
          <article className="raid-home-panel raid-home-attack-panel">
            <header><h3>공격 상태</h3><span>사용 {season.recordCount} · 남은 {season.remainingAttacks}</span></header>
            <RaidAttackStatus groups={season.attackGroups} />
          </article>
          <article className="raid-home-panel raid-home-recommend-panel">
            <header><h3>보스별 추천덱</h3><span>공용 스쿼드</span></header>
            <RecommendationPanel bosses={season.bosses} decks={season.recommendedDecks} />
          </article>
        </div>
      </div>
      {selectedBoss ? <RaidBossDetailModal boss={selectedBoss} records={season.records} onClose={() => setDetailBossNo(null)} /> : null}
    </section>
  );
}
